/**
 * opportunityDisplay — shared display helpers for the Opportunities module
 * (L1 list + L2 finding detail), Lane 42.
 *
 * DISPLAY ONLY. Every dollar/percent/count these helpers touch arrives
 * finished from the backend (the deterministic engine froze it into the
 * finding); nothing here computes, derives, rounds a value into a different
 * value, or reconciles. Formatting (currency symbol, thousands separators)
 * is the entire job.
 */

import { formatValue } from './formatValue';

// family_type -> the neutral finding STATEMENT (Lane 47 A2.5, Ryan-locked:
// no question framing anywhere — the tag says what to check, the team
// decides; "PO consolidation opportunity" / "Inconsistent pricing" /
// "Same-day price spread").
export const FAMILY_LABELS = {
  volume: 'PO consolidation opportunity',
  dispersion: 'Inconsistent pricing',
  same_day: 'Same-day price spread',
  leadtime: 'Lead time gap',           // Lane 148 (flag-gated family)
  spike_reversion: 'Price spike (reverted)',  // Lane 246/258 (flag-gated family)
  // Lane 323/338 (flag-gated family; always CLASS_EVIDENCE — claim-free
  // supplier-discussion talking point, never a savings opportunity).
  step_change: 'Sustained price step-up',
};

// family_type -> shared Badge variant (existing variants only — no new hexes)
export const FAMILY_BADGE_VARIANTS = {
  volume: 'primary',
  dispersion: 'warning',
  same_day: 'info',
  leadtime: 'default',                 // Lane 148
  spike_reversion: 'warning',          // Lane 258 — observed overpayment (amber)
  step_change: 'default',              // Lane 338 — evidence, not a warning
};

// disposition status -> human label / Badge variant
export const DISPOSITION_LABELS = {
  pursuing: 'Pursuing',
  needs_data: 'Needs data',
  accepted: 'Accepted',
};

export const DISPOSITION_BADGE_VARIANTS = {
  pursuing: 'primary',
  needs_data: 'warning',
  accepted: 'success',
};

// Whole-dollar amount: "$48,600" (missing -> em dash, per formatValue).
export function formatUsd(value) {
  return formatValue(value, 'currency');
}

// Unit price: "$12.50", but keep the frozen 4-decimal precision when it is
// significant ("$4.1035") so two different frozen prices can never render
// identically. Display-only trimming — never changes the value.
export function formatUnitPrice(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'Not available';
  const n = Number(value);
  const s4 = n.toFixed(4);
  return `$${s4.endsWith('00') ? n.toFixed(2) : s4}`;
}

// Quantity / count: thousands-separated integer.
export function formatQty(value) {
  return formatValue(value, 'integer');
}

// The design §5 honesty guard: the dollar column is FAMILY-LABELED so
// prospective (volume) and observed (dispersion/same-day) dollars never read
// as equivalent.
//
// QA R2-B (Mega QA 2 2026-07-22): `omitOpportunityNoun` drops the trailing
// "opportunity" word from the VOLUME label only. The L2 finding hero already
// carries an "Opportunity" header above the value, so "$X/yr opportunity"
// there is redundant -> "$X/yr". Every OTHER surface (L1 list, dossier callout)
// keeps the full label, and the family-specific "overpaid" / "tied up in stock"
// phrasings are untouched.
export function headlineLabel(familyType, headlineValue, { omitOpportunityNoun = false } = {}) {
  const usd = formatUsd(headlineValue);
  if (familyType === 'volume') return omitOpportunityNoun ? `${usd}/yr` : `${usd}/yr opportunity`;
  // Lane 148: the padded lead-time dollar is a working-capital figure (cash
  // tied up in early-ordered stock), not an overpayment — never mislabel it.
  if (familyType === 'leadtime') return `${usd} tied up in stock`;
  return `${usd} overpaid`;
}

// "Jul 1, 2026" from an ISO string; falls back to the raw string rather than
// fabricating a date. Date-ONLY strings ("2025-10-03", e.g. incident_date) are
// parsed as LOCAL dates — new Date("2025-10-03") would parse as UTC midnight
// and render the PREVIOUS day in US timezones.
export function formatDate(iso) {
  if (!iso) return 'Not available';
  const s = String(iso);
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// snapshot.trigger_source -> plain language (unknown values pass through raw).
export function triggerSourceLabel(source) {
  const labels = {
    manual: 'Manual refresh',
    upload: 'PO upload',
    po_upload: 'PO upload',
  };
  return labels[source] || source || 'Not available';
}
